import React, { useState, useMemo, useRef, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useReactToPrint } from "react-to-print";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  LabLetterhead,
  LabLetterheadBottom,
  PatientMetaCard,
  ReportSection,
  SummaryTab,
  BiomarkersTab,
  CriticalTab,
  LifestyleTab,
  PrintRunningHeader,
  PrintRunningFooter,
  getLetterhead,
} from "@/pages/report/SmartReport";
import { computeHealthScore, scoreBand } from "@/lib/health";
import { Monitor, FileText, Layers, Download, Mail, Loader2, Send } from "lucide-react";

/**
 * DUMMY_REPORT — a realistic sample smart report used to render the live preview.
 * Kept intentionally rich (multiple biomarkers, critical + healthy, lifestyle blocks)
 * so admins immediately see how their letterhead handles real content.
 */
const DUMMY_REPORT = {
  id: "preview-a1b2c3d4e5f6",
  patient_name: "Riya Sharma",
  patient_age: 32,
  patient_gender: "Female",
  patient_id: "P-2026-4821",
  patient_phone: "+91 98765 43210",
  report_date: "2026-02-06",
  reported_date: "2026-02-06",
  referred_by: "Dr. Anil Verma (MD Medicine)",
  package_name: "Preventive Full Body Checkup",
  reviewer_signatures: [],
  name_mismatch: false,
  smart_report: {
    overall_status: "Healthy with a few things to watch",
    executive_summary:
      "Riya's key vitals look great. HbA1c is slightly elevated — switching from refined carbs to whole grains and adding 30 min brisk walking 5 days a week will help bring it back into range within 3 months. Vitamin D is low; a 60,000 IU sachet weekly for 8 weeks is recommended.",
    smart_health_summary:
      "Riya's key vitals look great. HbA1c is slightly elevated — switching from refined carbs to whole grains and adding 30 min brisk walking 5 days a week will help bring it back into range within 3 months. Vitamin D is low; a 60,000 IU sachet weekly for 8 weeks is recommended.",
    summary_bullets: [
      "Blood sugar (HbA1c) is in the pre-diabetic zone at 5.9 — reversible with 3 months of diet + walking.",
      "Vitamin D is deficient at 18 ng/mL — plan a supplementation course.",
      "Thyroid, liver, kidney, blood counts and heart markers are all normal.",
      "LDL cholesterol slightly high; recommend cutting fried food & increasing fibre.",
    ],
    health_snapshot: [
      { name: "Thyroid", icon: "butterfly", status: "normal" },
      { name: "Liver", icon: "liver", status: "normal" },
      { name: "Kidney", icon: "kidney", status: "normal" },
      { name: "Blood Sugar", icon: "drop", status: "high" },
      { name: "Heart", icon: "heart", status: "high" },
      { name: "Hemoglobin", icon: "blood", status: "normal" },
    ],
    biomarkers: [
      { name: "HbA1c", value: 5.9, unit: "%", range: "4.0 – 5.6", status: "high", why_it_matters: "3-month average blood sugar" },
      { name: "Vitamin D", value: 18, unit: "ng/mL", range: "30 – 100", status: "low", why_it_matters: "Bone & immunity" },
      { name: "LDL Cholesterol", value: 132, unit: "mg/dL", range: "< 100", status: "high", why_it_matters: "Bad cholesterol" },
      { name: "Hemoglobin", value: 13.4, unit: "g/dL", range: "12 – 15.5", status: "normal", why_it_matters: "Oxygen carrier" },
      { name: "TSH", value: 2.1, unit: "µIU/mL", range: "0.4 – 4.5", status: "normal", why_it_matters: "Thyroid function" },
      { name: "Creatinine", value: 0.82, unit: "mg/dL", range: "0.6 – 1.1", status: "normal", why_it_matters: "Kidney function" },
    ],
    critical_findings: [],
    diet_recommendations: [
      "Swap white rice/roti with millets (bajra, ragi) for at least 1 meal a day",
      "Add 1 handful of walnuts or almonds daily for LDL",
      "Drink 3 litres of water daily, cut sugary drinks",
    ],
    physical_activity: [
      "30 minutes brisk walk, 5 days a week",
      "Strength training 2×/week (bodyweight squats, push-ups)",
      "Yoga / stretching on rest days",
    ],
    avoid_watch_out: [
      "Fried food, sweets and refined sugar",
      "Late-night meals — dinner before 8pm ideally",
    ],
    follow_up_tests: [],
  },
};

/**
 * LetterheadLivePreview — renders the DUMMY_REPORT inside a real smart-report shell
 * (letterhead top + PatientMetaCard + stacked sections + letterhead bottom) driven by
 * the *unsaved* form.letterhead. Lets admins iterate on branding with instant feedback.
 */
export default function LetterheadLivePreview({ form, compact = false, defaultMode = "a4" }) {
  const { user } = useAuth();
  const [mode, setMode] = useState(defaultMode);   // "screen" | "a4" | "pages" | "pdf" | "email"
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailAddr, setEmailAddr] = useState("");
  const [sending, setSending] = useState(false);
  const printRef = useRef(null);
  const emailTargetRef = useRef(null);

  // Prefill the email input the first time the user opens the Email mode.
  useEffect(() => {
    if (mode === "email" && !emailAddr) {
      setEmailAddr(form?.email || user?.email || "");
    }
  }, [mode, form?.email, user?.email, emailAddr]);

  // Build a synthetic "lab" object from the *unsaved* form so the preview reflects tweaks instantly.
  const previewLab = useMemo(() => ({ ...(form || {}), letterhead_mode: "standard" }), [form]);
  const report = DUMMY_REPORT;
  const smart = report.smart_report;
  const healthScore = useMemo(() => computeHealthScore(smart), [smart]);
  const band = scoreBand(healthScore);
  const lh = getLetterhead(previewLab) || { header_height_mm: 24, footer_height_mm: 16 };

  const reactPrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `${form?.name || "Lab"}_sample_letterhead`,
    pageStyle: `@page { size: A4; margin: 0; }`,
  });
  const handleExportPDF = () => {
    try {
      const p = reactPrint();
      if (p && typeof p.then === "function") p.catch(() => window.print());
    } catch (_) { window.print(); }
  };

  const openEmailDialog = () => {
    setEmailAddr(form?.email || user?.email || "");
    setEmailOpen(true);
  };

  const sendEmail = async () => {
    if (!emailAddr || !emailAddr.includes("@")) { toast.error("Enter a valid email"); return; }
    if (!emailTargetRef.current) { toast.error("Preview not ready"); return; }
    if (!user?.lab_id) { toast.error("Lab not linked to your account"); return; }
    setSending(true);
    try {
      // Render the visible email-target DOM into a canvas at high DPI, then pack as A4 PDF
      const canvas = await html2canvas(emailTargetRef.current, {
        scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false,
      });
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW = 210, pageH = 297;
      const imgW = pageW;
      const imgH = (canvas.height * imgW) / canvas.width;
      const imgData = canvas.toDataURL("image/jpeg", 0.85);
      let hLeft = imgH;
      let position = 0;
      // Paginate if the rendered canvas is taller than one A4 page
      pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
      hLeft -= pageH;
      while (hLeft > 0) {
        position = hLeft - imgH;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
        hLeft -= pageH;
      }
      const b64 = pdf.output("datauristring").split(",", 2)[1];
      await api.post(`/labs/${user.lab_id}/email-sample-pdf`, {
        to_email: emailAddr,
        pdf_base64: b64,
      });
      toast.success(`Sample PDF emailed to ${emailAddr}`);
      setEmailOpen(false);
    } catch (err) {
      toast.error(apiError(err, "Email send failed"));
    } finally { setSending(false); }
  };

  return (
    <Card className={`${compact ? "" : "mt-6"} rounded-2xl overflow-hidden`} data-testid="letterhead-live-preview">
      <div className="px-4 py-3 border-b border-border bg-muted/40 flex items-center justify-between flex-wrap gap-2">
        <div className="min-w-0">
          <div className="font-heading font-semibold text-ink flex items-center gap-2 text-sm"><FileText className="w-4 h-4 text-efgh-green" /> Live preview</div>
          <div className="text-[11px] text-ink/50 mt-0.5 truncate">Dummy patient · Riya Sharma · updates instantly</div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="flex flex-wrap rounded-md border border-border text-xs" data-testid="preview-mode-toggles">
            <ModeBtn mode={mode} setMode={setMode} value="screen" icon={Monitor} label="Screen" />
            <ModeBtn mode={mode} setMode={setMode} value="a4" icon={FileText} label="A4" />
            <ModeBtn mode={mode} setMode={setMode} value="pages" icon={Layers} label="Pages" />
            <ModeBtn mode={mode} setMode={setMode} value="pdf" icon={Download} label="PDF" />
            <ModeBtn mode={mode} setMode={setMode} value="email" icon={Mail} label="Email" />
          </div>
        </div>
      </div>
      <div className="bg-[#F2F3F5] p-4" data-testid={`preview-mode-${mode}`}>
        <div ref={emailTargetRef}>
          {mode === "screen" && <ScreenPreview lab={previewLab} report={report} smart={smart} healthScore={healthScore} band={band} />}
          {mode === "a4" && <A4Preview lab={previewLab} report={report} smart={smart} healthScore={healthScore} band={band} lh={lh} compact={compact} />}
          {mode === "pages" && <MultiPagePreview lab={previewLab} report={report} smart={smart} healthScore={healthScore} band={band} lh={lh} compact={compact} />}
          {mode === "pdf" && (
            <div className="space-y-3">
              <div className="rounded-lg border border-ink/10 bg-white px-3 py-2 flex items-center justify-between flex-wrap gap-2" data-testid="pdf-mode-panel">
                <div className="min-w-0">
                  <div className="text-sm font-heading font-semibold text-ink flex items-center gap-1.5"><Download className="w-3.5 h-3.5 text-ink/60" /> Final PDF preview</div>
                  <div className="text-[11px] text-ink/50">Mirrors what patients receive on download. Click below to generate a sample PDF file.</div>
                </div>
                <Button type="button" size="sm" onClick={handleExportPDF} data-testid="export-sample-pdf" className="h-8 px-3 text-xs bg-ink hover:bg-ink/90 text-white">
                  <Download className="w-3.5 h-3.5 mr-1" /> Download PDF
                </Button>
              </div>
              <A4Preview lab={previewLab} report={report} smart={smart} healthScore={healthScore} band={band} lh={lh} compact={compact} />
            </div>
          )}
          {mode === "email" && (
            <div className="space-y-3">
              <div className="rounded-lg border border-ink/10 bg-white px-3 py-3" data-testid="email-mode-panel">
                <div className="flex items-center gap-1.5 text-sm font-heading font-semibold text-ink"><Mail className="w-3.5 h-3.5 text-ink/60" /> Email a sample PDF</div>
                <p className="text-[11px] text-ink/50 mt-0.5">Preview how it will look when shared over email. We generate a PDF of the current design and send it as an attachment.</p>
                <div className="mt-3 grid sm:grid-cols-[1fr_auto] gap-2 items-end">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider text-ink/50">Recipient email</Label>
                    <Input type="email" value={emailAddr || form?.email || user?.email || ""} onChange={(e) => setEmailAddr(e.target.value)} placeholder="you@yourlab.in" data-testid="email-sample-input-inline" className="h-9 text-sm" />
                  </div>
                  <Button type="button" onClick={sendEmail} disabled={sending} data-testid="email-sample-send-inline" className="h-9 px-4 bg-ink hover:bg-ink/90 text-white">
                    {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Send
                  </Button>
                </div>
              </div>
              <A4Preview lab={previewLab} report={report} smart={smart} healthScore={healthScore} band={band} lh={lh} compact={compact} />
            </div>
          )}
        </div>
      </div>

      {/* Hidden print target — this DOM is what react-to-print renders into a PDF.
          We reuse the exact same components + print CSS so exported PDF is 1:1 with the smart-report print output. */}
      <div style={{ position: "fixed", left: "-10000px", top: 0, width: "210mm", background: "white" }} aria-hidden>
        <PrintTarget ref={printRef} lab={previewLab} report={report} smart={smart} healthScore={healthScore} band={band} lh={lh} />
      </div>

      <Dialog open={emailOpen} onOpenChange={(o) => !sending && setEmailOpen(o)}>
        <DialogContent data-testid="email-sample-dialog" className="sm:max-w-md">
          <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4 text-efgh-green" /> Send sample PDF to email</DialogTitle>
          <div className="mt-2">
            <p className="text-sm text-ink/60">We&apos;ll generate a PDF of the current preview and email it to you as an attachment. Perfect for sharing letterhead proofs with partners.</p>
            <div className="mt-4">
              <Label>Recipient email</Label>
              <Input type="email" value={emailAddr} onChange={(e) => setEmailAddr(e.target.value)} placeholder="you@yourlab.in" data-testid="email-sample-input" />
              <p className="text-[11px] text-ink/40 mt-1">Prefilled with your lab&apos;s contact email — change it to send to anyone.</p>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEmailOpen(false)} disabled={sending}>Cancel</Button>
              <Button type="button" onClick={sendEmail} disabled={sending} data-testid="email-sample-send" className="bg-ink hover:bg-ink/90 text-white">
                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Send PDF
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ===== The hidden component that react-to-print actually clones into the print window ===== */
const PrintTarget = React.forwardRef(function PrintTarget({ lab, report, smart, healthScore, band, lh }, ref) {
  const hH = lh.header_height_mm ?? 24;
  const fH = lh.footer_height_mm ?? 16;
  const mT = lh.page_margin_top_mm ?? 0;
  const mR = lh.page_margin_right_mm ?? 8;
  const mB = lh.page_margin_bottom_mm ?? 0;
  const mL = lh.page_margin_left_mm ?? 8;
  const secGap = lh.section_gap_mm ?? 5;
  const pageSize = lh.page_size || "A4";
  return (
    <div ref={ref} className="bg-white">
      <style>{`
        @media print {
          @page { size: ${pageSize}; margin: 0; }
          html, body { background: white !important; }
          .lp-running-header { position: fixed !important; top: ${mT}mm; left: 0; right: 0; z-index: 9999; background: white !important; }
          .lp-running-footer { position: fixed !important; bottom: ${mB}mm; left: 0; right: 0; z-index: 9999; background: white !important; }
          .lp-report-content { padding-top: ${hH + mT + 2}mm !important; padding-bottom: ${fH + mB + 2}mm !important; padding-left: ${mL}mm !important; padding-right: ${mR}mm !important; overflow: visible !important; }
          .lp-report-sections { row-gap: ${secGap}mm !important; display: block !important; }
          .lp-report-sections [data-section-wrap] { page-break-inside: auto !important; break-inside: auto !important; margin-top: ${secGap}mm !important; background: transparent !important; box-shadow: none !important; }
          .lp-report-sections [data-section-wrap]:first-child { margin-top: 0 !important; }
          .lp-report-content .bg-white, .lp-report-content [class*="shadow"] { background: transparent !important; box-shadow: none !important; }
        }
      `}</style>
      <div className="lp-running-header"><PrintRunningHeader lab={lab} report={report} /></div>
      <div className="lp-running-footer"><PrintRunningFooter lab={lab} report={report} /></div>
      <div className="lp-report-content" style={{ position: "relative", zIndex: 1 }}>
        <PatientMetaCard report={report} smart={smart} healthScore={healthScore} band={band} />
        <div className="lp-report-sections flex flex-col gap-4 mt-4">
          <ReportSection title="Smart Health Summary"><SummaryTab smart={smart} healthScore={healthScore} /></ReportSection>
          <ReportSection title="Biomarker Details"><BiomarkersTab smart={smart} history={[]} onSelect={() => {}} /></ReportSection>
          <ReportSection title="Lifestyle & Food"><LifestyleTab smart={smart} /></ReportSection>
        </div>
      </div>
    </div>
  );
});

function ModeBtn({ mode, setMode, value, icon: Icon, label }) {
  return (
    <button type="button" onClick={() => setMode(value)} data-testid={`preview-mode-btn-${value}`} className={`px-2.5 py-1.5 flex items-center gap-1 border-r last:border-r-0 border-border ${mode === value ? "bg-ink text-white" : "bg-white text-ink/60 hover:bg-muted"}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

/* ================= SCREEN VIEW ================= */
function ScreenPreview({ lab, report, smart, healthScore, band }) {
  const lh = getLetterhead(lab) || {};
  const secGap = lh.section_gap_mm ?? 5;
  // Render at a fixed desktop-like inner width (896px = max-w-4xl) and scale to fit container.
  const BASE_W = 896;
  const ref = useRef(null);
  const [scale, setScale] = useState(0.5);
  const [innerHeight, setInnerHeight] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const el = ref.current;
    const inner = el.querySelector("[data-screen-inner]");
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(Math.min(1, w / BASE_W));
      if (inner) setInnerHeight(inner.offsetHeight);
    };
    update();
    const roC = new ResizeObserver(update);
    roC.observe(el);
    if (inner) roC.observe(inner);
    return () => roC.disconnect();
  }, []);
  return (
    <div ref={ref} className="w-full" style={{ height: innerHeight ? `${innerHeight * scale}px` : "auto", overflow: "hidden" }}>
      <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: `${BASE_W}px` }}>
        <div data-screen-inner className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="-mx-0"><LabLetterhead lab={lab} report={report} /></div>
          <div className="px-8 py-6">
            <PatientMetaCard report={report} smart={smart} healthScore={healthScore} band={band} />
            <div className="mt-6 flex flex-col" style={{ gap: `${secGap * 4}px` }}>
              <ReportSection title="Smart Health Summary" testid="preview-section-summary"><SummaryTab smart={smart} healthScore={healthScore} /></ReportSection>
              <ReportSection title="Biomarker Details" testid="preview-section-biomarkers"><BiomarkersTab smart={smart} history={[]} onSelect={() => {}} /></ReportSection>
              {(smart.critical_findings || []).length > 0 && (
                <ReportSection title="Critical Findings" testid="preview-section-critical"><CriticalTab smart={smart} history={[]} onSelect={() => {}} /></ReportSection>
              )}
              <ReportSection title="Lifestyle & Food" testid="preview-section-lifestyle"><LifestyleTab smart={smart} /></ReportSection>
            </div>
          </div>
          <LabLetterheadBottom lab={lab} report={report} />
        </div>
      </div>
    </div>
  );
}

/* ================= AUTO-SCALE HOOK =================
 * Observes container width and computes a CSS transform scale so the fixed-size A4 frame
 * (210mm wide ≈ 793.7px at 96dpi) always fits perfectly inside its container while preserving
 * true A4 proportions. Returns [containerRef, scale].
 */
const A4_MM_TO_PX = 96 / 25.4; // 96 CSS px per inch / 25.4mm per inch = 3.7795
const A4_WIDTH_PX = 210 * A4_MM_TO_PX;   // 793.7px
const A4_HEIGHT_PX = 297 * A4_MM_TO_PX;  // 1122.5px

function useAutoScale(maxScale = 1) {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.5);
  useEffect(() => {
    if (!ref.current) return undefined;
    const el = ref.current;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) setScale(Math.min(maxScale, w / A4_WIDTH_PX));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxScale]);
  return [ref, scale];
}

/* ================= A4 PRINT VIEW (single page) ================= */
function A4Preview({ lab, report, smart, healthScore, band, lh, compact = false }) {
  const hH = lh.header_height_mm ?? 24;
  const fH = lh.footer_height_mm ?? 16;
  const mT = lh.page_margin_top_mm ?? 0;
  const mR = lh.page_margin_right_mm ?? 8;
  const mB = lh.page_margin_bottom_mm ?? 0;
  const mL = lh.page_margin_left_mm ?? 8;
  const secGap = lh.section_gap_mm ?? 5;
  // At most: full-scale when compact=false; keep to 1.0 max for compact so we still shrink to fit but never blow up beyond real size.
  const [wrapRef, scale] = useAutoScale(1);
  return (
    <div ref={wrapRef} className="w-full" style={{ height: `${A4_HEIGHT_PX * scale}px`, overflow: "hidden" }}>
      <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
        <div
          className="bg-white shadow-xl relative"
          style={{
            width: `${A4_WIDTH_PX}px`,
            height: `${A4_HEIGHT_PX}px`,
            position: "relative",
            overflow: "hidden",
            fontFamily: "system-ui, sans-serif",
          }}
        >
          {/* Watermark (Phase C) */}
          {lh.watermark_url && (
            <div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: `url(${lh.watermark_url})`, backgroundRepeat: "no-repeat", backgroundPosition: "center", backgroundSize: "40%", opacity: lh.watermark_opacity ?? 0.06, pointerEvents: "none", zIndex: 0 }} />
          )}
          {/* Fixed header (mimics print running header) */}
          <div style={{ position: "absolute", top: `${mT}mm`, left: 0, right: 0, height: `${hH}mm`, background: "white", zIndex: 20 }}>
            <PrintPreviewHeader lab={lab} report={report} />
          </div>
          {/* Fixed footer (mimics print running footer) */}
          <div style={{ position: "absolute", bottom: `${mB}mm`, left: 0, right: 0, height: `${fH}mm`, background: "white", zIndex: 20 }}>
            <PrintPreviewFooter lab={lab} report={report} />
          </div>
          {/* Content — padded to leave room for header/footer */}
          <div style={{ paddingTop: `${hH + mT + 3}mm`, paddingBottom: `${fH + mB + 3}mm`, paddingLeft: `${mL}mm`, paddingRight: `${mR}mm`, position: "relative", zIndex: 1 }}>
            <PatientMetaCard report={report} smart={smart} healthScore={healthScore} band={band} />
            <div className="mt-4 flex flex-col" style={{ gap: `${secGap}mm` }}>
              <ReportSection title="Smart Health Summary"><SummaryTab smart={smart} healthScore={healthScore} /></ReportSection>
              <ReportSection title="Biomarker Details"><BiomarkersTab smart={smart} history={[]} onSelect={() => {}} /></ReportSection>
              <ReportSection title="Lifestyle & Food"><LifestyleTab smart={smart} /></ReportSection>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= MULTI-PAGE VIEW ================= */
function MultiPagePreview({ lab, report, smart, healthScore, band, lh, compact = false }) {
  const hH = lh.header_height_mm ?? 24;
  const fH = lh.footer_height_mm ?? 16;
  const mT = lh.page_margin_top_mm ?? 0;
  const mR = lh.page_margin_right_mm ?? 8;
  const mB = lh.page_margin_bottom_mm ?? 0;
  const mL = lh.page_margin_left_mm ?? 8;
  const secGap = lh.section_gap_mm ?? 5;
  const [wrapRef, scale] = useAutoScale(1);
  const pageContent = (pg) => (
    <div className="bg-white shadow-xl relative" style={{ width: `${A4_WIDTH_PX}px`, height: `${A4_HEIGHT_PX}px`, overflow: "hidden", fontFamily: "system-ui, sans-serif" }}>
      {lh.watermark_url && (
        <div aria-hidden style={{ position: "absolute", inset: 0, backgroundImage: `url(${lh.watermark_url})`, backgroundRepeat: "no-repeat", backgroundPosition: "center", backgroundSize: "40%", opacity: lh.watermark_opacity ?? 0.06, pointerEvents: "none", zIndex: 0 }} />
      )}
      <div style={{ position: "absolute", top: `${mT}mm`, left: 0, right: 0, height: `${hH}mm`, background: "white", zIndex: 20 }}>
        <PrintPreviewHeader lab={lab} report={report} />
      </div>
      <div style={{ position: "absolute", bottom: `${mB}mm`, left: 0, right: 0, height: `${fH}mm`, background: "white", zIndex: 20 }}>
        <PrintPreviewFooter lab={lab} report={report} />
      </div>
      <div style={{ paddingTop: `${hH + mT + 3}mm`, paddingBottom: `${fH + mB + 3}mm`, paddingLeft: `${mL}mm`, paddingRight: `${mR}mm`, position: "relative", zIndex: 1 }}>
        <div className="text-[10px] text-ink/40 uppercase tracking-widest mb-2">Page {pg} of 2 · preview</div>
        {pg === 1 ? (
          <>
            <PatientMetaCard report={report} smart={smart} healthScore={healthScore} band={band} />
            <div className="mt-4 flex flex-col" style={{ gap: `${secGap}mm` }}>
              <ReportSection title="Smart Health Summary"><SummaryTab smart={smart} healthScore={healthScore} /></ReportSection>
            </div>
          </>
        ) : (
          <div className="flex flex-col" style={{ gap: `${secGap}mm` }}>
            <ReportSection title="Biomarker Details"><BiomarkersTab smart={smart} history={[]} onSelect={() => {}} /></ReportSection>
            <ReportSection title="Lifestyle & Food"><LifestyleTab smart={smart} /></ReportSection>
          </div>
        )}
      </div>
    </div>
  );
  const pageHeight = A4_HEIGHT_PX * scale;
  const gapPx = 16; // gap between the two mini pages
  return (
    <div ref={wrapRef} className="w-full" style={{ height: `${pageHeight * 2 + gapPx}px`, overflow: "hidden" }}>
      {[1, 2].map((pg, i) => (
        <div key={pg} style={{ marginTop: i === 0 ? 0 : `${gapPx}px`, height: `${pageHeight}px`, overflow: "hidden" }} data-testid={`preview-page-${pg}`}>
          <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: `${A4_WIDTH_PX}px`, height: `${A4_HEIGHT_PX}px` }}>
            {pageContent(pg)}
          </div>
        </div>
      ))}
    </div>
  );
}

/* Print-preview strips — reuse the same underlying render but strip the `hidden print:block` gate
   so the strips actually render inside the on-screen A4 frame. */
function PrintPreviewHeader({ lab, report }) {
  return (
    <div style={{ visibility: "visible" }}>
      <div className="[&_.hidden]:!block [&_.print\\:block]:!block">
        <PrintRunningHeader lab={lab} report={report} />
      </div>
    </div>
  );
}
function PrintPreviewFooter({ lab, report }) {
  return (
    <div style={{ visibility: "visible" }}>
      <div className="[&_.hidden]:!block [&_.print\\:block]:!block">
        <PrintRunningFooter lab={lab} report={report} />
      </div>
    </div>
  );
}
